import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, Trash2, ChevronDown } from '@digital-twins/design-system/icons'
import { intlLocaleForUi } from '@/i18n'
import { Button } from '@/components/ui/button'
import {
  AUDIENCE_WIZARD_BUTTON_CLASS,
  AUDIENCE_WIZARD_BUTTON_VARIANT,
} from '@/lib/constants/audience-wizard'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { SegmentFilterQuestionPicker } from '@/components/audiences/SegmentFilterQuestionPicker'

import type { AudienceTag, AudienceTagCondition } from '@digital-twins/shared-types'
import type { FilterChipGroup } from '@/types/components/audiences/filter-chip-grid'
import type { SegmentFilterQuestion } from '@/lib/utils/segment-filter-questions'

export interface ConditionDraft {
  sourceColumn: string
  values: Array<string>
  /**
   * When set, this draft represents an ENTIRE group rather than a single
   * column. On save it expands to one condition per member column, all
   * sharing `values` (a respondent must match the chosen values on every
   * member column of the group).
   */
  groupId?: string
  memberColumns?: Array<string>
  /** User-chosen subset. Undefined means legacy/all members; empty is incomplete. */
  selectedMembers?: Array<string>
}

interface AddTagModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  columnSamples: Record<string, string[]>
  columnLabels?: Record<string, string>
  columnValueLabels?: Record<string, Record<string, string>>
  /**
   * Full filterable question universe (singles + groups) from the shared filter
   * builder. When provided, the column picker mirrors the filter "Add/Hide
   * column" dropdown exactly: only filterable columns are offered and grouped
   * columns collapse under their group. When omitted, falls back to every
   * column present in `columnSamples`.
   */
  questions?: Array<SegmentFilterQuestion>
  existingTag?: AudienceTag | null
  onPreview?: (
    conditions: Array<AudienceTagCondition>,
    signal: AbortSignal,
  ) => Promise<TagPreviewResult>
  onSave: (tag: AudienceTag) => void
}

export interface TagPreviewResult {
  matchingCount: number
  totalCount: number
}

export function expandTagConditionDrafts(
  conditions: Array<ConditionDraft>,
): Array<AudienceTagCondition> {
  const expanded: Array<AudienceTagCondition> = []
  for (const condition of conditions) {
    if (condition.values.length === 0) continue
    if (condition.groupId && condition.memberColumns?.length) {
      const members = condition.selectedMembers === undefined
        ? condition.memberColumns
        : condition.selectedMembers
      for (const sourceColumn of members) {
        expanded.push({ sourceColumn, values: condition.values })
      }
    } else if (condition.sourceColumn) {
      expanded.push({
        sourceColumn: condition.sourceColumn,
        values: condition.values,
      })
    }
  }
  return expanded
}

export function AddTagModal({
  open,
  onOpenChange,
  columnSamples,
  columnLabels,
  columnValueLabels,
  questions,
  existingTag,
  onPreview,
  onSave,
}: AddTagModalProps) {
  const { t, i18n } = useTranslation('app')
  const [tagName, setTagName] = useState('')
  const [conditions, setConditions] = useState<Array<ConditionDraft>>([
    { sourceColumn: '', values: [] },
  ])
  const [previewResult, setPreviewResult] = useState<TagPreviewResult | null>(null)
  const [previewStatus, setPreviewStatus] = useState<'idle' | 'pending' | 'unavailable'>('idle')
  const previewRequestRef = useRef(0)

  // Groups derived from the shared filter question universe — grouped columns
  // collapse under their group entry, exactly like the filter dropdown.
  const columnGroups = useMemo<Array<FilterChipGroup>>(() => {
    if (!questions) return []
    return questions
      .filter((q) => q.kind === 'group')
      .map((q) => ({
        groupId: q.id,
        label: q.label,
        memberColumns: q.options.map((o) => o.sourceColumn),
        options: q.options.map((o) => ({
          sourceColumn: o.sourceColumn,
          optionLabel: o.optionLabel,
          sampleValues: o.sampleValues,
          valueLabels: o.valueLabels,
          valueMap: o.valueMap,
        })),
      }))
  }, [questions])

  // Ungrouped, filterable single columns. When `questions` is provided we mirror
  // the filter universe (excludes operational / non-filterable columns); group
  // members are intentionally excluded here and surfaced via `columnGroups`.
  const availableColumns = useMemo(() => {
    const source = questions
      ? questions
          .filter((q) => q.kind === 'single')
          .map((q) => q.options[0]?.sourceColumn)
          .filter((col): col is string => Boolean(col))
      : Object.keys(columnSamples).filter(
          (col) => (columnSamples[col]?.length ?? 0) >= 1,
        )
    return source.sort((a, b) => {
      const labelA = columnLabels?.[a] ?? a
      const labelB = columnLabels?.[b] ?? b
      return labelA.localeCompare(labelB)
    })
  }, [questions, columnSamples, columnLabels])

  // Re-collapse persisted single-column conditions back into a whole-group
  // draft when they exactly cover a group's member columns with identical
  // values. Keeps editing symmetric with how the group was created.
  const collapseConditions = useCallback(
    (conds: Array<AudienceTagCondition>): Array<ConditionDraft> => {
      const valueKey = (values: Array<string>) =>
        [...values]
          .map((v) => v.trim().toLowerCase())
          .sort()
          .join('\u0000')

      const groupDraftByFirstCol = new Map<string, ConditionDraft>()
      const consumedCols = new Set<string>()

      for (const group of columnGroups) {
        const presentMembers = group.memberColumns.filter((col) =>
          conds.some((c) => c.sourceColumn === col),
        )
        if (presentMembers.length === 0) continue

        const matched = presentMembers.map((col) =>
          conds.find((c) => c.sourceColumn === col)!,
        )
        const firstKey = valueKey(matched[0].values)
        if (!matched.every((m) => valueKey(m.values) === firstKey)) continue

        const firstCol = presentMembers[0]
        const isSubset = presentMembers.length < group.memberColumns.length
        groupDraftByFirstCol.set(firstCol, {
          sourceColumn: '',
          values: [...matched[0].values],
          groupId: group.groupId,
          memberColumns: group.memberColumns,
          selectedMembers: isSubset ? presentMembers : group.memberColumns,
        })
        for (const col of presentMembers) consumedCols.add(col)
      }

      const result: Array<ConditionDraft> = []
      for (const c of conds) {
        const groupDraft = groupDraftByFirstCol.get(c.sourceColumn)
        if (groupDraft) {
          result.push(groupDraft)
          continue
        }
        if (consumedCols.has(c.sourceColumn)) continue
        result.push({ sourceColumn: c.sourceColumn, values: [...c.values] })
      }
      return result.length > 0 ? result : [{ sourceColumn: '', values: [] }]
    },
    [columnGroups],
  )

  useEffect(() => {
    if (!open) return
    if (existingTag) {
      setTagName(existingTag.name)
      setConditions(
        existingTag.conditions.length > 0
          ? collapseConditions(existingTag.conditions)
          : [{ sourceColumn: '', values: [] }],
      )
    } else {
      setTagName('')
      setConditions([{ sourceColumn: '', values: [] }])
    }
    // collapseConditions is intentionally excluded: re-running on a late
    // `questions` load would clobber in-progress edits. We only seed on open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existingTag])

  const getColumnLabel = useCallback(
    (col: string) => columnLabels?.[col] ?? col,
    [columnLabels],
  )

  const getValueLabel = useCallback(
    (col: string, val: string) => {
      const labels = columnValueLabels?.[col]
      if (!labels) return val
      return labels[val] ?? val
    },
    [columnValueLabels],
  )

  const updateCondition = useCallback(
    (index: number, update: Partial<ConditionDraft>) => {
      setConditions((prev) =>
        prev.map((c, i) => (i === index ? { ...c, ...update } : c)),
      )
    },
    [],
  )

  const addCondition = useCallback(() => {
    setConditions((prev) => [...prev, { sourceColumn: '', values: [] }])
  }, [])

  const removeCondition = useCallback((index: number) => {
    setConditions((prev) => {
      if (prev.length <= 1) return prev
      return prev.filter((_, i) => i !== index)
    })
  }, [])

  const toggleValue = useCallback(
    (conditionIndex: number, value: string) => {
      setConditions((prev) =>
        prev.map((c, i) => {
          if (i !== conditionIndex) return c
          const next = c.values.includes(value)
            ? c.values.filter((v) => v !== value)
            : [...c.values, value]
          return { ...c, values: next }
        }),
      )
    },
    [],
  )

  const isConditionFilled = useCallback(
    (c: ConditionDraft) =>
      c.values.length > 0 &&
      (Boolean(c.sourceColumn) ||
        (Boolean(c.groupId) &&
          (c.memberColumns?.length ?? 0) > 0 &&
          (c.selectedMembers === undefined || c.selectedMembers.length > 0))),
    [],
  )

  const isValid = useMemo(() => {
    if (!tagName.trim()) return false
    return conditions.some(isConditionFilled)
  }, [tagName, conditions, isConditionFilled])

  useEffect(() => {
    const requestId = ++previewRequestRef.current
    setPreviewResult(null)

    if (!open || !onPreview) {
      setPreviewStatus('idle')
      return
    }

    const expandedConditions = expandTagConditionDrafts(conditions)
    if (expandedConditions.length === 0) {
      setPreviewStatus('idle')
      return
    }

    setPreviewStatus('pending')
    const controller = new AbortController()
    const timeout = window.setTimeout(async () => {
      try {
        const result = await onPreview(expandedConditions, controller.signal)
        if (
          controller.signal.aborted ||
          requestId !== previewRequestRef.current
        ) {
          return
        }
        setPreviewResult(result)
        setPreviewStatus('idle')
      } catch {
        if (
          controller.signal.aborted ||
          requestId !== previewRequestRef.current
        ) {
          return
        }
        setPreviewStatus('unavailable')
      }
    }, 300)

    return () => {
      window.clearTimeout(timeout)
      controller.abort()
    }
  }, [open, onPreview, conditions])

  const handleSave = useCallback(() => {
    if (!isValid) return

    onSave({
      id: existingTag?.id ?? crypto.randomUUID(),
      name: tagName.trim(),
      conditions: expandTagConditionDrafts(conditions),
      color: existingTag?.color,
    })
    onOpenChange(false)
  }, [isValid, tagName, conditions, existingTag, onSave, onOpenChange])

  const previewMessage = useMemo(() => {
    if (!onPreview || (previewStatus === 'idle' && !previewResult)) return null
    if (previewStatus === 'pending') return t('audienceCreate.tagPreviewPending')
    if (previewStatus === 'unavailable') return t('audienceCreate.tagPreviewUnavailable')
    if (!previewResult) return null

    const locale = intlLocaleForUi(i18n.resolvedLanguage)
    const percentage = previewResult.totalCount === 0
      ? 0
      : (previewResult.matchingCount / previewResult.totalCount) * 100
    return t('audienceCreate.tagPreviewResult', {
      matching: new Intl.NumberFormat(locale).format(previewResult.matchingCount),
      total: new Intl.NumberFormat(locale).format(previewResult.totalCount),
      percentage: new Intl.NumberFormat(locale, {
        maximumFractionDigits: 2,
      }).format(percentage),
    })
  }, [onPreview, previewStatus, previewResult, t, i18n.resolvedLanguage])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl gap-0 p-0 sm:p-0 overflow-hidden flex flex-col max-h-[calc(85vh-20px)]">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle className="font-display text-xl font-normal">
            {existingTag ? t('audienceCreate.editTag') : t('audienceCreate.createAudienceTag')}
          </DialogTitle>
          <DialogDescription>
            {t('audienceCreate.tagsHelper').split('\n')[0]}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5 px-6 pb-4 overflow-y-auto flex-1 min-h-0">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="tag-name-input">{t('audienceCreate.tagName')}</Label>
            <Input
              id="tag-name-input"
              data-testid="add-tag-name-input"
              className="h-9 text-sm"
              placeholder={t('audienceCreate.tagNamePlaceholder')}
              value={tagName}
              onChange={(e) => setTagName(e.target.value)}
              autoFocus
            />
          </div>

          <div className="flex flex-col gap-3">
            <Label>{t('audienceCreate.conditions')}</Label>
            <p className="text-sm text-muted-foreground -mt-2">
              {t('audienceCreate.conditionsAllMustMatch')}
            </p>

            {conditions.map((condition, condIndex) => (
              <div key={condIndex} className="flex flex-col gap-3">
                {condIndex > 0 && (
                  <div className="flex items-center gap-2">
                    <div className="h-px flex-1 bg-border/60" />
                    <span className="text-xs font-semibold tracking-wide text-muted-foreground">
                      {t('audienceCreate.andLogic')}
                    </span>
                    <div className="h-px flex-1 bg-border/60" />
                  </div>
                )}
                <ConditionRow
                  index={condIndex}
                  condition={condition}
                  totalConditions={conditions.length}
                  questions={questions}
                  columnSamples={columnSamples}
                  columnGroups={columnGroups}
                  getValueLabel={getValueLabel}
                  onUpdate={updateCondition}
                  onToggleValue={toggleValue}
                  onRemove={removeCondition}
                  canRemove={conditions.length > 1}
                />
              </div>
            ))}

            <Button
              type="button"
              variant="outline"
              size="sm"
              className="self-start"
              data-testid="add-tag-add-condition-button"
              onClick={addCondition}
            >
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              {t('audienceCreate.addCondition')}
            </Button>
            {previewMessage && (
              <p
                className="text-sm text-muted-foreground"
                role="status"
                aria-live="polite"
                data-testid="add-tag-preview-status"
              >
                {previewMessage}
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="px-6 pb-6 pt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="add-tag-cancel-button"
          >
            {t('audienceCreate.cancel')}
          </Button>
          <Button
            variant={AUDIENCE_WIZARD_BUTTON_VARIANT}
            className={AUDIENCE_WIZARD_BUTTON_CLASS}
            disabled={!isValid}
            onClick={handleSave}
            data-testid="add-tag-save-button"
          >
            {existingTag ? t('audienceCreate.saveChanges') : t('audienceCreate.createTag')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ConditionRow({
  index,
  condition,
  totalConditions,
  questions,
  columnSamples,
  columnGroups,
  getValueLabel,
  onUpdate,
  onToggleValue,
  onRemove,
  canRemove,
}: {
  index: number
  condition: ConditionDraft
  totalConditions: number
  questions?: Array<SegmentFilterQuestion>
  columnSamples: Record<string, string[]>
  columnGroups?: Array<FilterChipGroup>
  getValueLabel: (col: string, val: string) => string
  onUpdate: (index: number, update: Partial<ConditionDraft>) => void
  onToggleValue: (conditionIndex: number, value: string) => void
  onRemove: (index: number) => void
  canRemove: boolean
}) {
  const { t } = useTranslation('app')
  const [valuesOpen, setValuesOpen] = useState(false)
  const [optionsOpen, setOptionsOpen] = useState(false)

  // The group currently selected as a WHOLE (vs. a single member column).
  const selectedWholeGroup = useMemo(() => {
    if (!condition.groupId) return null
    return (columnGroups ?? []).find((g) => g.groupId === condition.groupId) ?? null
  }, [condition.groupId, columnGroups])

  const effectiveMembers = condition.selectedMembers ?? condition.memberColumns ?? []

  // Column used to resolve value labels — a representative member for groups.
  const valueLabelColumn = selectedWholeGroup
    ? (effectiveMembers[0] ?? selectedWholeGroup.memberColumns[0] ?? '')
    : condition.sourceColumn

  // Selectable values for the current selection. For a whole-group selection we
  // offer the union of selected member columns' values (the chosen values then
  // apply to all selected member columns).
  const valuesForColumn = useMemo(() => {
    let values: Array<string> = []
    if (selectedWholeGroup) {
      const memberSet = new Set(effectiveMembers)
      const set = new Set<string>()
      for (const opt of selectedWholeGroup.options) {
        if (memberSet.size > 0 && !memberSet.has(opt.sourceColumn)) continue
        for (const v of opt.sampleValues ?? []) set.add(v)
        for (const v of columnSamples[opt.sourceColumn] ?? []) set.add(v)
      }
      values = Array.from(set)
    } else if (condition.sourceColumn) {
      values = [...(columnSamples[condition.sourceColumn] ?? [])]
    }
    return values.sort((a, b) => {
      if (a === '__MISSING__') return 1
      if (b === '__MISSING__') return -1
      return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
    })
  }, [selectedWholeGroup, effectiveMembers, condition.sourceColumn, columnSamples])

  return (
    <div
      className="flex items-start gap-2"
      data-testid={`add-tag-condition-row-${index}`}
    >
      <div className="flex flex-1 flex-col gap-2 min-w-0">
        {totalConditions > 1 && (
          <span className="text-xs font-medium text-muted-foreground">
            {t('audienceCreate.conditionN', { n: index + 1 })}
          </span>
        )}
        {/* Column picker */}
        <SegmentFilterQuestionPicker
          questions={questions ?? []}
          value={{
            sourceColumn: condition.sourceColumn || undefined,
            groupId: condition.groupId,
          }}
          onSelectGroup={(question) => {
            const members = question.options.map((o) => o.sourceColumn)
            onUpdate(index, {
              sourceColumn: '',
              values: [],
              groupId: question.id,
              memberColumns: members,
              selectedMembers: [],
            })
          }}
          onSelectColumn={(col) => {
            onUpdate(index, {
              sourceColumn: col,
              values: [],
              groupId: undefined,
              memberColumns: undefined,
              selectedMembers: undefined,
            })
          }}
          data-testid={`add-tag-condition-column-${index}`}
        />

        {/* Options sub-selector — only for whole-group selections */}
        {selectedWholeGroup && selectedWholeGroup.options.length > 0 && (
          <>
            <span className="text-xs font-medium text-muted-foreground px-0.5">{t('audienceCreate.forOptions')}</span>
            <Popover open={optionsOpen} onOpenChange={setOptionsOpen} modal>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full min-w-0 justify-between"
                  data-testid={`add-tag-condition-options-${index}`}
                >
                  <span className="w-0 grow truncate text-left">
                    {effectiveMembers.length === selectedWholeGroup.memberColumns.length
                      ? t('audienceCreate.allOptionsCount', { count: selectedWholeGroup.memberColumns.length })
                      : effectiveMembers.length > 0
                        ? selectedWholeGroup.options
                            .filter((o) => effectiveMembers.includes(o.sourceColumn))
                            .map((o) => o.optionLabel)
                            .join(', ')
                        : t('audienceCreate.selectOptions')}
                  </span>
                  <ChevronDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="w-[var(--radix-popover-trigger-width)] p-0"
                align="start"
                onOpenAutoFocus={(e) => e.preventDefault()}
              >
                <div className="max-h-[280px] overflow-y-auto overscroll-contain p-1">
                  {selectedWholeGroup.options.map((opt) => {
                    const isSelected = effectiveMembers.includes(opt.sourceColumn)
                    return (
                      <button
                        key={opt.sourceColumn}
                        type="button"
                        className={cn(
                          'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent',
                          isSelected && 'bg-accent/60',
                        )}
                        onClick={() => {
                          const current = condition.selectedMembers ?? condition.memberColumns ?? []
                          const next = current.includes(opt.sourceColumn)
                            ? current.filter((c) => c !== opt.sourceColumn)
                            : [...current, opt.sourceColumn]
                          onUpdate(index, {
                            selectedMembers: next,
                            values: condition.values,
                          })
                        }}
                      >
                        <span
                          className={cn(
                            'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                            isSelected
                              ? 'border-primary bg-primary text-primary-foreground'
                              : 'border-input',
                          )}
                        >
                          {isSelected && (
                            <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                              <path
                                d="M1 4L3.5 6.5L9 1"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </span>
                        <span className="truncate">{opt.optionLabel}</span>
                      </button>
                    )
                  })}
                </div>
              </PopoverContent>
            </Popover>
          </>
        )}

        <span className="text-xs font-medium text-muted-foreground px-0.5">{t('audienceCreate.hasToBe')}</span>

        {/* Value multi-select */}
        <Popover open={valuesOpen} onOpenChange={setValuesOpen} modal>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="w-full min-w-0 justify-between"
              disabled={!condition.sourceColumn && (!selectedWholeGroup || effectiveMembers.length === 0)}
              data-testid={`add-tag-condition-values-${index}`}
            >
              <span className="w-0 grow truncate text-left">
                {condition.values.length > 0
                  ? condition.values
                      .map((v) => getValueLabel(valueLabelColumn, v))
                      .join(', ')
                  : t('audienceCreate.selectValues')}
              </span>
              <ChevronDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[var(--radix-popover-trigger-width)] p-0"
            align="start"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <div className="max-h-[280px] overflow-y-auto overscroll-contain p-1">
              {valuesForColumn.map((val) => {
                const isSelected = condition.values.includes(val)
                return (
                  <button
                    key={val}
                    type="button"
                    className={cn(
                      'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent',
                      isSelected && 'bg-accent/60',
                    )}
                    onClick={() => onToggleValue(index, val)}
                  >
                    <span
                      className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                        isSelected
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-input',
                      )}
                    >
                      {isSelected && (
                        <svg
                          width="10"
                          height="8"
                          viewBox="0 0 10 8"
                          fill="none"
                        >
                          <path
                            d="M1 4L3.5 6.5L9 1"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </span>
                    <span className="truncate">
                      {getValueLabel(valueLabelColumn, val)}
                    </span>
                  </button>
                )
              })}
              {valuesForColumn.length === 0 && (
                <p className="px-2 py-3 text-center text-sm text-muted-foreground">
                  {t('audienceCreate.noValuesAvailable')}
                </p>
              )}
            </div>
          </PopoverContent>
        </Popover>

        {selectedWholeGroup && (
          <p className="px-0.5 text-xs text-muted-foreground">
            {effectiveMembers.length === selectedWholeGroup.memberColumns.length
              ? t('audienceCreate.appliesToAllQuestions', { count: selectedWholeGroup.memberColumns.length })
              : t('audienceCreate.appliesToSomeQuestions', {
                  selected: effectiveMembers.length,
                  total: selectedWholeGroup.memberColumns.length,
                })}
          </p>
        )}
      </div>

      {canRemove && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive mt-0.5"
          onClick={() => onRemove(index)}
          data-testid={`add-tag-remove-condition-${index}`}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  )
}
